import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect as connectRedux } from 'react-redux'
import _some from 'lodash/some'
import Button, { TYPES } from '../Button/index'

/**
 * A component to load more data when clicked or optionally when visible
 * for infinite scrolling
 */
class LoadMore extends Component {
  componentWillUnmount = () => {
    this.removeIntersectionObserver()
    this._component = undefined
  }

  /**
   * Set the react dom element when it is created
   * @param {Element} component A DOM element
   */
  onRef = (component) => {
    const { visibilityThreshold, infiniteScroll } = this.props
    this.removeIntersectionObserver()
    this._component = component
    if (!infiniteScroll || !component || !global.IntersectionObserver) {
      return
    }
    this._observer = new IntersectionObserver(this.onIntersectionObserverVisible, {
      // Use viewport
      root: null,
      rootMargin: '0% 0% 50% 0%',
      threshold: visibilityThreshold,
    })
    this._observer.observe(component)
  }

  /**
   * Trigger the load more action when the component
   * is visible and not already processing
   */
  onIntersectionObserverVisible = (entries) => {
    const { onLoadMore, processing } = this.props
    if (processing) {
      return
    }
    // Loop through entires to see if one is visible
    const isVisible = _some(entries, (entry) => {
      const { isIntersecting } = entry
      return isIntersecting
    })
    if (!isVisible) {
      return
    }
    onLoadMore()
  }

  /**
   * Handle button click
   * @param {Event} e The click event
   */
  onClickButton = (e) => {
    const { onLoadMore } = this.props
    e.stopPropagation()
    e.preventDefault()
    onLoadMore()
  }

  /**
   * Remove the intersection observer if it exists
   */
  removeIntersectionObserver = () => {
    if (this._observer && this._component) {
      this._observer.unobserve(this._component)
      this._observer = undefined
    }
  }

  render () {
    const { props } = this
    const {
      text,
      defaultText,
      buttonClassName,
      currentCount,
      totalCount,
      processing,
      infiniteScroll,
    } = props
    const loadMoreText = text || defaultText
    const styles = {}

    if (currentCount === 0 || processing || currentCount === totalCount) {
      return null
    }

    // if there is support for IntersectionObserver, make the button invisible
    if (infiniteScroll && global.IntersectionObserver) {
      styles.visibility = 'hidden'
    }

    return (
      <div ref={this.onRef} className="load-more" style={styles}>
        <Button
          type={TYPES.SECONDARY}
          onClick={this.onClickButton}
          className={buttonClassName.join(' ')}
          data-action="load-more"
        >
          {loadMoreText}
        </Button>
      </div>
    )
  }
}

LoadMore.propTypes = {
  text: PropTypes.string,
  defaultText: PropTypes.string,
  onLoadMore: PropTypes.func.isRequired,
  buttonClassName: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  currentCount: PropTypes.number.isRequired,
  processing: PropTypes.bool,
  infiniteScroll: PropTypes.bool,
  visibilityThreshold: PropTypes.number,
}

LoadMore.defaultProps = {
  visibilityThreshold: 0.01,
  buttonClassName: [],
}

/**
 * @type {LoadMore}
 */
export default connectRedux((state) => ({
  defaultText: state.staticText.getIn([
    'data',
    'loadMore',
    'data',
    'defaultText',
  ], ''),
}))(LoadMore)
